import {
  AnimatePresence,
  type AnimatePresenceProps,
  type HTMLMotionProps,
  motion,
} from "framer-motion";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/**
 * This file is copied and modified from https://github.com/chakra-ui/chakra-ui/blob/v2/packages/components/src/tooltip/tooltip.tsx
 * Its the only chakra v2 component we actually used. Its pretty bloated since I just took everything, likely a lot unnecessary parts
 * as well, however you would likely not need to modify the file.
 */

type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

export type TooltipProps = {
  children: React.ReactNode;
  /** The tooltip content */
  label?: React.ReactNode;
  /** Accessible label for screen readers (replaces visible role/id on tooltip) */
  "aria-label"?: string;
  /** Show an arrow tip @default false */
  hasArrow?: boolean;
  /** Preferred placement @default "bottom" */
  placement?: Placement;
  /** Delay before showing (ms) @default 0 */
  openDelay?: number;
  /** Delay before hiding (ms) @default 0 */
  closeDelay?: number;
  /** Close on click @default true */
  closeOnClick?: boolean;
  /** Close on pressing Escape @default true */
  closeOnEsc?: boolean;
  /** Control open state externally */
  isOpen?: boolean;
  /** Default open state for uncontrolled usage */
  defaultIsOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  /** If true, tooltip stays open while hovering it */
  isDisabled?: boolean;
  /** Offset from trigger in px @default 8 */
  offset?: number;
  /** Props forwarded to framer-motion's motion.div */
  motionProps?: HTMLMotionProps<"div">;
  /** Props forwarded to AnimatePresence */
  animatePresenceProps?: AnimatePresenceProps;

  style?: React.CSSProperties;
};

const ARROW_SIZE = 8;

const scaleVariants = {
  exit: {
    scale: 0.85,
    opacity: 0,
    transition: { duration: 0.1, easings: "easeOut" },
  },
  enter: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.15, easings: "easeIn" },
  },
};

function getPosition(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  placement: Placement,
  offset: number,
) {
  const [side, align] = placement.split("-") as [string, string | undefined];

  let top = 0;
  let left = 0;

  switch (side) {
    case "top":
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case "bottom":
      top = triggerRect.bottom + offset;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case "left":
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    case "right":
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + offset;
      break;
    default:
      break;
  }

  // Alignment adjustments for start/end
  if (side === "top" || side === "bottom") {
    if (align === "start") left = triggerRect.left;
    if (align === "end") left = triggerRect.right - tooltipRect.width;
  } else {
    if (align === "start") top = triggerRect.top;
    if (align === "end") top = triggerRect.bottom - tooltipRect.height;
  }

  return { top: top + window.scrollY, left: left + window.scrollX };
}

function setRef<T>(target: React.Ref<T> | undefined, value: T | null) {
  if (typeof target === "function") {
    target(value);
  } else if (target) {
    const mutable: React.MutableRefObject<T | null> =
      target as React.MutableRefObject<T | null>;
    mutable.current = value;
  }
}

function getTransformOrigin(placement: Placement): string {
  if (placement.startsWith("top")) return "bottom center";
  if (placement.startsWith("bottom")) return "top center";
  if (placement.startsWith("left")) return "center right";
  return "center left";
}

function getArrowStyle(placement: Placement): React.CSSProperties {
  const side = placement.split("-")[0];
  const base: React.CSSProperties = {
    position: "absolute",
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    background: "inherit",
    transform: "rotate(45deg)",
  };

  switch (side) {
    case "top":
      return {
        ...base,
        bottom: -ARROW_SIZE / 2,
        left: "50%",
        marginLeft: -ARROW_SIZE / 2,
      };
    case "bottom":
      return {
        ...base,
        top: -ARROW_SIZE / 2,
        left: "50%",
        marginLeft: -ARROW_SIZE / 2,
      };
    case "left":
      return {
        ...base,
        right: -ARROW_SIZE / 2,
        top: "50%",
        marginTop: -ARROW_SIZE / 2,
      };
    case "right":
      return {
        ...base,
        left: -ARROW_SIZE / 2,
        top: "50%",
        marginTop: -ARROW_SIZE / 2,
      };
    default:
      return base;
  }
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      label,
      "aria-label": ariaLabel,
      hasArrow = false,
      placement = "bottom",
      openDelay = 0,
      closeDelay = 0,
      closeOnClick = true,
      closeOnEsc = true,
      isOpen: isOpenProp,
      defaultIsOpen = false,
      onOpen,
      onClose,
      isDisabled,
      offset: offsetProp = 8,
      motionProps,
      animatePresenceProps,
      style,
    },
    ref,
  ) => {
    const isControlled = isOpenProp !== undefined;
    const [isOpenState, setIsOpenState] = useState(defaultIsOpen);
    const isOpen = isControlled ? isOpenProp : isOpenState;

    const triggerRef = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const openTimeout = useRef<ReturnType<typeof setTimeout>>();
    const closeTimeout = useRef<ReturnType<typeof setTimeout>>();

    const [position, setPosition] = useState({ top: 0, left: 0 });

    const tooltipId = useId();

    const open = useCallback(() => {
      if (isDisabled) return;
      clearTimeout(closeTimeout.current);
      if (openDelay > 0) {
        openTimeout.current = setTimeout(() => {
          if (!isControlled) setIsOpenState(true);
          onOpen?.();
        }, openDelay);
      } else {
        if (!isControlled) setIsOpenState(true);
        onOpen?.();
      }
    }, [isDisabled, openDelay, isControlled, onOpen]);

    const close = useCallback(() => {
      clearTimeout(openTimeout.current);
      if (closeDelay > 0) {
        closeTimeout.current = setTimeout(() => {
          if (!isControlled) setIsOpenState(false);
          onClose?.();
        }, closeDelay);
      } else {
        if (!isControlled) setIsOpenState(false);
        onClose?.();
      }
    }, [closeDelay, isControlled, onClose]);

    // Calculate position when open
    useEffect(() => {
      if (!isOpen) return;
      const trigger = triggerRef.current;
      const tooltip = tooltipRef.current;
      if (!trigger || !tooltip) return;

      const update = () => {
        const triggerRect = trigger.getBoundingClientRect();
        // Use offset dimensions instead of getBoundingClientRect to avoid
        // measuring the tooltip while it's mid-scale animation (which would
        // return 85% of the actual width/height and shift the position).
        const tooltipRect = {
          width: tooltip.offsetWidth,
          height: tooltip.offsetHeight,
        } as DOMRect;
        setPosition(
          getPosition(triggerRect, tooltipRect, placement, offsetProp),
        );
      };

      // Initial + raf to ensure tooltip has rendered
      requestAnimationFrame(update);
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [isOpen, placement, offsetProp]);

    // Close on Escape
    useEffect(() => {
      if (!isOpen || !closeOnEsc) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") close();
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [isOpen, closeOnEsc, close]);

    // Cleanup timeouts
    useEffect(
      () => () => {
        clearTimeout(openTimeout.current);
        clearTimeout(closeTimeout.current);
      },
      [],
    );

    const triggerProps = {
      onMouseEnter: open,
      onMouseLeave: close,
      onFocus: open,
      onBlur: close,
      onClick: closeOnClick ? close : undefined,
      "aria-describedby": isOpen ? tooltipId : undefined,
    };

    if (!label) {
      return children;
    }

    const hasAriaLabel = !!ariaLabel;

    const tooltipStyle: React.CSSProperties = {
      position: "absolute",
      top: position.top,
      left: position.left,
      zIndex: 1500,
      pointerEvents: "none",
      maxWidth: 320,
      borderRadius: 4,
      fontSize: 16,
      lineHeight: 1.4,
      transformOrigin: getTransformOrigin(placement),
      ...style,
    };

    return (
      <>
        <span
          ref={triggerRef as React.Ref<HTMLSpanElement>}
          style={{ display: "inline-block" }}
          {...triggerProps}
        >
          {children}
        </span>
        {createPortal(
          <AnimatePresence {...animatePresenceProps}>
            {isOpen && (
              <motion.div
                ref={(node) => {
                  setRef(tooltipRef, node);
                  setRef(ref, node);
                }}
                id={hasAriaLabel ? undefined : tooltipId}
                role={hasAriaLabel ? undefined : "tooltip"}
                variants={scaleVariants}
                initial="exit"
                animate="enter"
                exit="exit"
                style={tooltipStyle}
                {...motionProps}
              >
                {label}
                {hasAriaLabel && (
                  <span
                    id={tooltipId}
                    role="tooltip"
                    style={{
                      position: "absolute",
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: "hidden",
                      clip: "rect(0,0,0,0)",
                      whiteSpace: "nowrap",
                      borderWidth: 0,
                    }}
                  >
                    {ariaLabel}
                  </span>
                )}
                {hasArrow && <span style={getArrowStyle(placement)} />}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
      </>
    );
  },
);

Tooltip.displayName = "Tooltip";
